/**
 * Confirmación destructiva con motivo obligatorio — el nivel `⚠⚠`.
 *
 * MATRIZ_INTERACCIONES.md §1.2 define tres niveles. El `⚠` simple lo cubre
 * `ConfirmDialog` del design system tal cual; este resuelve el `⚠⚠`, que el
 * DS no cubre porque necesita un campo: **consecuencia externa o legal, o
 * pérdida de acceso, con motivo de al menos 10 caracteres.**
 *
 * Son once acciones (§4 de la matriz) y todas piden lo mismo, así que piden
 * lo mismo aquí una vez en lugar de once veces.
 *
 * El mínimo de 10 se valida también en el servidor (`motivoSchema`). Repetirlo
 * aquí no es duplicar una regla de negocio: es evitar un viaje para decir algo
 * que ya se sabe.
 */
import { useEffect, useId, useState } from 'react'
import type { ReactNode } from 'react'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Textarea,
} from '@mpfn/ui'
import { FormField } from '@mpfn/patterns'

const MINIMO_MOTIVO = 10

interface PropsDialogo {
  abierto: boolean
  onCambio: (abierto: boolean) => void
  titulo: string
  descripcion: string
  /** Obligatorio y nombra la acción: nada de «Aceptar» (contrato del DS). */
  etiquetaConfirmar: string
  /** Advertencia sobre la consecuencia. Va en un `Callout` del llamante. */
  advertencia?: ReactNode
  cargando?: boolean
  onConfirmar: (motivo: string) => void | Promise<void>
}

export function DialogoConMotivo({
  abierto,
  onCambio,
  titulo,
  descripcion,
  etiquetaConfirmar,
  advertencia,
  cargando = false,
  onConfirmar,
}: PropsDialogo) {
  const idMotivo = useId()
  const [motivo, setMotivo] = useState('')
  const [tocado, setTocado] = useState(false)

  // Al cerrar se limpia: si no, reabrirlo para otra fila muestra el motivo
  // de la anterior y se acaba registrando el texto equivocado.
  useEffect(() => {
    if (!abierto) {
      setMotivo('')
      setTocado(false)
    }
  }, [abierto])

  const recortado = motivo.trim()
  const valido = recortado.length >= MINIMO_MOTIVO
  const error = tocado && !valido ? `Escriba el motivo, con al menos ${MINIMO_MOTIVO} caracteres.` : null

  return (
    <Dialog open={abierto} onOpenChange={onCambio}>
      <DialogContent>
        <DialogTitle>{titulo}</DialogTitle>
        <DialogDescription>{descripcion}</DialogDescription>

        {advertencia}

        <FormField id={idMotivo} label="Motivo" required {...(error ? { error } : {})}>
          <Textarea
            rows={3}
            value={motivo}
            onChange={(evento) => {
              setMotivo(evento.target.value)
            }}
            onBlur={() => {
              setTocado(true)
            }}
            placeholder="Quedará registrado en la auditoría."
          />
        </FormField>

        <div className="sigfisc-dialogo__acciones">
          <DialogClose asChild>
            <Button variant="secondary" type="button">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            type="button"
            loading={cargando}
            onClick={() => {
              setTocado(true)
              if (!valido) return
              void onConfirmar(recortado)
            }}
          >
            {etiquetaConfirmar}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
